/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { events } from '@adobe-commerce/event-bus';
import { state } from '@/cart/lib/state';
import { config, fetchGraphQl, getCartData, getStoreConfig } from '@/cart/api';
import { MERGE_CARTS_MUTATION } from './graphql/MergeCartsMutation';
import { CartModel } from '@/cart/data/models';

export const initializeCart = async (): Promise<CartModel | null> => {
  if (state.initializing) return null;

  state.initializing = true;

  // set config
  if (!state.config) {
    state.config = await getStoreConfig();
  }

  const payload = state.authenticated
    ? await getCustomerCartPayload()
    : await getGuestCartPayload();

  events.emit('cart/initialized', payload);
  events.emit('cart/data', payload);

  state.initializing = false;

  return payload;
};

export async function getCustomerCartPayload() {
  const existingCartId = state.cartId;

  const cart = await getCartData();

  if (!cart) return null;

  // set cartid in the state
  state.cartId = cart.id;

  // If the existing cart is the same as the fetched cart, return the fetched cart
  if (!existingCartId || cart.id === existingCartId) return cart;

  // If the existing cart is not the same as the fetched cart, merge the carts
  return await fetchGraphQl(MERGE_CARTS_MUTATION, {
    variables: {
      guestCartId: existingCartId,
      customerCartId: cart.id,
    },
  })
    .then(() => {
      return getCartData();
    })
    .then((transformedCartData) => {
      const payload = {
        oldCartItems: cart.items,
        newCart: transformedCartData,
      };
      events.emit('cart/merged', payload);
      return transformedCartData;
    })
    .catch(() => {
      // gracefully handle merge error, continue with existing cart
      console.error('Could not merge carts');
      return cart;
    });
}

export async function getGuestCartPayload() {
  // If guest cart is not enabled, return null
  if (config.getConfig().disableGuestCart === true) {
    return null;
  }

  // If there is not a cart id in the state, return null
  if (!state.cartId) {
    return null;
  }

  // If there is a cart id in the state, return the cart data
  try {
    return await getCartData();
  } catch (error) {
    console.error(error);
    return null;
  }
}
